import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  EmptyRow, ErpTable, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtNumber } from '@/shared/lib/format'
import {
  attendanceBases, eventStatuses, eventTypes, listEvents, saveEvent, type CampaignEvent,
} from './api'

/**
 * Rallies, meetings and everything else the campaign held.
 *
 * ## Why an event is its own row
 *
 * The first aspirant project hung the head count off the expense, because the expense was
 * the only object it had. A rally was then unrepresentable except as something somebody paid
 * for: two expenses at one rally read as two rallies, and a rally that cost nothing never
 * happened. Here the event is the fact and spend points at it.
 *
 * ## The head count always shows how it was arrived at
 *
 * A count at the gate and an eye estimate from the stage are different claims. The page
 * never shows the number alone, because a figure without its basis gets quoted as though it
 * were the strongest reading available.
 */
export function EventsPage() {
  const canRead = useCan('finance:read')
  const canRecord = useCan('finance:record')
  const [editing, setEditing] = useState<CampaignEvent | 'new' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [params, setParams] = useSearchParams()

  const events = useLoad(() => listEvents({ size: 200 }), [])

  /*
   * `?open=<id>` opens that event once the list has loaded. The calendar links here with a
   * specific rally, and without this it landed on the diary and left somebody to find the
   * thing they had just clicked.
   *
   * Keyed on the id rather than a ran-once flag so a second link to a different event, from
   * a calendar still open in another tab, opens the second one.
   */
  const openId = params.get('open')
  useEffect(() => {
    if (!openId) return
    const match = (events.data?.content ?? []).find((e) => e.id === openId)
    if (match) setEditing(match)
  }, [openId, events.data])

  if (!canRead) return <Forbidden what="Campaign events" />

  const rows = events.data?.content ?? []

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Rallies & meetings"
        description="What was held, where, how many came — and what it cost. Attendance against
                     the ward's registered voters is the cheapest honest signal you have about
                     where support is real."
        right={canRecord ? (
          <button type="button" className="erp-btn-primary h-9 text-erp-sm"
                  onClick={() => setEditing('new')}>
            Add an event
          </button>
        ) : undefined}
      />

      <ErrorStrip message={error ?? events.error} />

      {events.loading && rows.length === 0 ? (
        <LoadingBlock label="Loading the diary…" />
      ) : (
        <Panel>
          <ErpTable head={(
            <>
              <Th>Event</Th>
              <Th>When</Th>
              <Th>Where</Th>
              <Th className="text-right">Expected</Th>
              <Th className="text-right">Came</Th>
              <Th>How counted</Th>
              <Th className="text-right">Cost</Th>
              <Th />
            </>
          )}>
            {rows.length === 0 ? (
              <EmptyRow colSpan={8}
                message="No events yet. Add the first rally or meeting, then spending can be
                         attached to it." />
            ) : rows.map((ev) => (
              <tr key={ev.id}>
                <Td>
                  <span className="font-medium">{ev.title}</span>
                  <div className="text-erp-xs text-muted-foreground">
                    {ev.code} · {ev.eventTypeCode.toLowerCase().replace(/_/g, ' ')}
                    {ev.statusCode !== 'HELD' ? ` · ${ev.statusCode.toLowerCase()}` : ''}
                  </div>
                </Td>
                <Td className="tabular-nums">
                  {new Date(ev.startsAt).toLocaleDateString()}
                </Td>
                <Td>
                  {ev.venue ?? <span className="text-muted-foreground">—</span>}
                  {ev.unitName ? (
                    <div className="text-erp-xs text-muted-foreground">{ev.unitName}</div>
                  ) : null}
                </Td>
                <Td className="text-right tabular-nums">
                  {ev.expectedAttendance == null ? '—' : fmtNumber(ev.expectedAttendance)}
                </Td>
                <Td className="text-right tabular-nums">
                  {ev.actualAttendance == null ? '—' : fmtNumber(ev.actualAttendance)}
                </Td>
                <Td className="text-erp-xs text-muted-foreground">
                  {/* Never the number without its basis. */}
                  {ev.actualAttendance == null ? '—'
                    : ev.attendanceBasisCode == null ? 'not stated'
                      : ev.attendanceBasisCode.toLowerCase().replace(/_/g, ' ')}
                </Td>
                <Td className="text-right tabular-nums">
                  {ev.spend == null || ev.expenseCount === 0 ? (
                    <span className="text-muted-foreground">nothing recorded</span>
                  ) : (
                    <>{ev.spendCurrency} {fmtNumber(Math.round(ev.spend))}</>
                  )}
                </Td>
                <Td>
                  {canRecord ? (
                    <button type="button" onClick={() => setEditing(ev)}
                            className="text-erp-xs underline underline-offset-2
                                       text-muted-foreground hover:text-foreground">
                      Edit
                    </button>
                  ) : null}
                </Td>
              </tr>
            ))}
          </ErpTable>
        </Panel>
      )}

      {editing && (
        <EventDialog
          event={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null)
            if (openId) {
              const next = new URLSearchParams(params)
              next.delete('open')
              setParams(next, { replace: true })
            }
          }}
          onDone={() => { setEditing(null); void events.reload() }}
          onError={setError}
        />
      )}
    </div>
  )
}

function EventDialog({ event, onClose, onDone, onError }: {
  event: CampaignEvent | null
  onClose: () => void
  onDone: () => void
  onError: (message: string | null) => void
}) {
  const types = useLoad(eventTypes, [])
  const statuses = useLoad(eventStatuses, [])
  const bases = useLoad(attendanceBases, [])
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState({
    title: event?.title ?? '',
    eventTypeCode: event?.eventTypeCode ?? '',
    statusCode: event?.statusCode ?? 'PLANNED',
    startsAt: (event?.startsAt ?? new Date().toISOString()).slice(0, 16),
    endsAt: event?.endsAt ? event.endsAt.slice(0, 16) : '',
    venue: event?.venue ?? '',
    expectedAttendance: event?.expectedAttendance?.toString() ?? '',
    actualAttendance: event?.actualAttendance?.toString() ?? '',
    attendanceBasisCode: event?.attendanceBasisCode ?? '',
    notes: event?.notes ?? '',
  })

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement
    | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    onError(null)
    try {
      await saveEvent({
        title: form.title,
        eventTypeCode: form.eventTypeCode,
        statusCode: form.statusCode || null,
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
        venue: form.venue || null,
        expectedAttendance: form.expectedAttendance ? Number(form.expectedAttendance) : null,
        actualAttendance: form.actualAttendance ? Number(form.actualAttendance) : null,
        attendanceBasisCode: form.attendanceBasisCode || null,
        notes: form.notes || null,
      }, event?.id)
      onDone()
    } catch (err) {
      onError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto
                    bg-black/40 p-6" role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl rounded border border-border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <h2 className="text-erp-base font-medium">
            {event ? `Edit ${event.code}` : 'Add an event'}
          </h2>
          <button type="button" onClick={onClose}
                  className="text-erp-sm text-muted-foreground hover:text-foreground">
            Close
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <L label="What it is">
              <input required value={form.title} onChange={set('title')} className="erp-input"
                     placeholder="e.g. Kabarnet market rally" />
            </L>
            <L label="Kind">
              <select required value={form.eventTypeCode} onChange={set('eventTypeCode')}
                      className="erp-input">
                <option value="">Choose…</option>
                {(types.data ?? []).map((t) => (
                  <option key={t.code} value={t.code}>{t.label}</option>
                ))}
              </select>
            </L>
            <L label="Starts">
              <input type="datetime-local" required value={form.startsAt}
                     onChange={set('startsAt')} className="erp-input" />
            </L>
            <L label="Ends">
              <input type="datetime-local" value={form.endsAt} onChange={set('endsAt')}
                     className="erp-input" />
            </L>
            <L label="Venue">
              <input value={form.venue} onChange={set('venue')} className="erp-input" />
            </L>
            <L label="Status">
              <select value={form.statusCode} onChange={set('statusCode')} className="erp-input">
                {(statuses.data ?? []).map((s) => (
                  <option key={s.code} value={s.code}>{s.label}</option>
                ))}
              </select>
            </L>
            <L label="How many we expected">
              <input type="number" min="0" value={form.expectedAttendance}
                     onChange={set('expectedAttendance')} className="erp-input" />
            </L>
            <L label="How many came">
              <input type="number" min="0" value={form.actualAttendance}
                     onChange={set('actualAttendance')} className="erp-input" />
            </L>
          </div>
          <L label="How that number was arrived at"
             hint="A count at the gate and an estimate from the stage are different claims, and
                   the figure gets quoted either way. Say which it was.">
            <select value={form.attendanceBasisCode} onChange={set('attendanceBasisCode')}
                    className="erp-input">
              <option value="">Not stated</option>
              {(bases.data ?? []).map((b) => (
                <option key={b.code} value={b.code}>{b.label}</option>
              ))}
            </select>
          </L>
          <L label="Notes">
            <textarea value={form.notes} onChange={set('notes')} rows={2}
                      className="erp-input" />
          </L>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
                    className="erp-btn-secondary h-9 text-erp-sm">Cancel</button>
            <button type="submit" disabled={busy}
                    className="erp-btn-primary h-9 text-erp-sm">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function L({ label, hint, children }: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="block text-erp-xs font-medium">{label}</span>
      {hint ? <span className="block text-erp-xs text-muted-foreground">{hint}</span> : null}
      <span className="mt-1 block">{children}</span>
    </label>
  )
}
